/**
 * Notifications Store
 *
 * A centralized store for in-app notifications that persists to localStorage.
 */

// Notification types
export type NotificationType = 'upload' | 'drop_closed' | 'drop_closing_soon' | 'team_invite' | 'system'

export interface Notification {
  id: string
  type: NotificationType
  title: string
  message: string
  dropId?: string
  dropName?: string
  createdAt: Date
  read: boolean
}

// Storage key for localStorage persistence
const NOTIFICATIONS_STORAGE_KEY = 'photodrop_notifications'

// Serialize notifications to localStorage-friendly format
function serializeNotifications(notifications: Notification[]): string {
  return JSON.stringify(notifications.map(n => ({
    ...n,
    createdAt: n.createdAt.toISOString(),
  })))
}

// Deserialize notifications from localStorage
function deserializeNotifications(data: string): Notification[] {
  try {
    const parsed = JSON.parse(data)
    return parsed.map((n: Record<string, unknown>) => ({
      ...n,
      createdAt: new Date(n.createdAt as string),
    }))
  } catch {
    return []
  }
}

// Get all notifications from storage
export function getNotifications(): Notification[] {
  const stored = localStorage.getItem(NOTIFICATIONS_STORAGE_KEY)
  if (stored) {
    return deserializeNotifications(stored)
  }
  return []
}

// Save notifications to storage
export function saveNotifications(notifications: Notification[]): void {
  localStorage.setItem(NOTIFICATIONS_STORAGE_KEY, serializeNotifications(notifications))
}

// Add a new notification
export function addNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'read'>): Notification {
  const notifications = getNotifications()
  const newNotification: Notification = {
    ...notification,
    id: `notif-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date(),
    read: false,
  }
  notifications.unshift(newNotification)
  // Keep only the most recent 50 notifications
  const trimmed = notifications.slice(0, 50)
  saveNotifications(trimmed)
  return newNotification
}

// Mark a notification as read
export function markNotificationAsRead(id: string): boolean {
  const notifications = getNotifications()
  const index = notifications.findIndex(n => n.id === id)
  if (index === -1) return false

  notifications[index].read = true
  saveNotifications(notifications)
  return true
}

// Mark all notifications as read
export function markAllNotificationsAsRead(): void {
  const notifications = getNotifications()
  notifications.forEach(n => n.read = true)
  saveNotifications(notifications)
}

// Get unread notification count
export function getUnreadCount(): number {
  const notifications = getNotifications()
  return notifications.filter(n => !n.read).length
}

// Delete a notification
export function deleteNotification(id: string): boolean {
  const notifications = getNotifications()
  const filtered = notifications.filter(n => n.id !== id)
  if (filtered.length === notifications.length) return false

  saveNotifications(filtered)
  return true
}

// Clear all notifications
export function clearAllNotifications(): void {
  localStorage.removeItem(NOTIFICATIONS_STORAGE_KEY)
}

// Add upload notification (helper for when contributor uploads)
export function addUploadNotification(dropId: string, dropName: string, photoCount: number): Notification {
  return addNotification({
    type: 'upload',
    title: 'New photos uploaded',
    message: `${photoCount} photo${photoCount > 1 ? 's were' : ' was'} uploaded to "${dropName}"`,
    dropId,
    dropName,
  })
}

// Add drop closed notification
export function addDropClosedNotification(dropId: string, dropName: string): Notification {
  return addNotification({
    type: 'drop_closed',
    title: 'Drop closed',
    message: `"${dropName}" has been closed`,
    dropId,
    dropName,
  })
}

// Add drop closing soon notification (for contributor reminders)
export function addDropClosingSoonNotification(
  dropId: string,
  dropName: string,
  closingTime: Date,
  _contributorToken: string
): Notification {
  const timeUntilClose = getTimeUntilClose(closingTime)
  return addNotification({
    type: 'drop_closing_soon',
    title: 'Drop closing soon!',
    message: `"${dropName}" is closing in ${timeUntilClose}. Upload your photos now before it's too late!`,
    dropId,
    dropName,
  })
}

// Helper to format time until close
function getTimeUntilClose(closingTime: Date): string {
  const now = new Date()
  const diffMs = closingTime.getTime() - now.getTime()
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

  if (diffHours >= 24) {
    const days = Math.floor(diffHours / 24)
    return `${days} day${days !== 1 ? 's' : ''}`
  } else if (diffHours >= 1) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''}`
  } else if (diffMinutes > 0) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}`
  }
  return 'less than a minute'
}

// Check for drops closing soon and notify contributors
// This is meant to be called periodically (e.g., on app load)
const CLOSING_SOON_THRESHOLD_HOURS = 1 // Notify when 1 hour or less until close
const CLOSING_SOON_NOTIFIED_KEY = 'photodrop_closing_soon_notified'

// Get which drop/invitation combos have already been notified
function getNotifiedClosingSoon(): Record<string, boolean> {
  try {
    const stored = localStorage.getItem(CLOSING_SOON_NOTIFIED_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// Mark a drop/invitation as notified
function markClosingSoonNotified(dropId: string, invitationId: string): void {
  const notified = getNotifiedClosingSoon()
  notified[`${dropId}-${invitationId}`] = true
  localStorage.setItem(CLOSING_SOON_NOTIFIED_KEY, JSON.stringify(notified))
}

// Check if we've already notified for this drop/invitation
function hasNotifiedClosingSoon(dropId: string, invitationId: string): boolean {
  const notified = getNotifiedClosingSoon()
  return notified[`${dropId}-${invitationId}`] === true
}

// Main function to check and notify contributors about closing drops
// Returns an array of notifications that were sent
export interface ContributorClosingNotification {
  dropId: string
  dropName: string
  contributorEmail?: string
  contributorPhone?: string
  contributorName?: string
  closingTime: Date
  contributorToken: string
}

export function checkAndNotifyClosingDrops(
  drops: Array<{
    id: string
    name: string
    expiresAt?: Date
    status: string
    contributorToken: string
  }>,
  invitations: Array<{
    id: string
    dropId: string
    recipientEmail?: string
    recipientPhone?: string
    recipientName?: string
    status: string
  }>
): ContributorClosingNotification[] {
  const notifications: ContributorClosingNotification[] = []
  const now = new Date()
  const thresholdMs = CLOSING_SOON_THRESHOLD_HOURS * 60 * 60 * 1000

  for (const drop of drops) {
    // Only check active drops with expiration dates
    if (drop.status !== 'active' || !drop.expiresAt) continue

    const timeUntilClose = drop.expiresAt.getTime() - now.getTime()

    // Check if drop is closing within threshold
    if (timeUntilClose > 0 && timeUntilClose <= thresholdMs) {
      // Find pending invitations for this drop
      const dropInvitations = invitations.filter(
        inv => inv.dropId === drop.id && inv.status === 'pending'
      )

      for (const invitation of dropInvitations) {
        // Skip if already notified
        if (hasNotifiedClosingSoon(drop.id, invitation.id)) continue

        // Create notification
        const notification: ContributorClosingNotification = {
          dropId: drop.id,
          dropName: drop.name,
          contributorEmail: invitation.recipientEmail,
          contributorPhone: invitation.recipientPhone,
          contributorName: invitation.recipientName,
          closingTime: drop.expiresAt,
          contributorToken: drop.contributorToken,
        }

        notifications.push(notification)

        // Mark as notified
        markClosingSoonNotified(drop.id, invitation.id)

        // Add in-app notification (for demo purposes)
        addDropClosingSoonNotification(
          drop.id,
          drop.name,
          drop.expiresAt,
          drop.contributorToken
        )

        // In production, this would also send actual email/SMS
        console.log(
          `[Demo] Closing soon notification sent to ${invitation.recipientEmail || invitation.recipientPhone} for drop "${drop.name}"`
        )
      }
    }
  }

  return notifications
}

// Clear closing soon notifications cache (for testing)
export function clearClosingSoonNotifications(): void {
  localStorage.removeItem(CLOSING_SOON_NOTIFIED_KEY)
}

// Update drop name in all related notifications
export function updateDropNameInNotifications(dropId: string, oldName: string, newName: string): number {
  const notifications = getNotifications()
  let updatedCount = 0

  notifications.forEach(n => {
    if (n.dropId === dropId) {
      // Update the dropName field
      n.dropName = newName
      // Update the message if it contains the old name
      if (n.message.includes(`"${oldName}"`)) {
        n.message = n.message.replace(`"${oldName}"`, `"${newName}"`)
      }
      updatedCount++
    }
  })

  if (updatedCount > 0) {
    saveNotifications(notifications)
  }

  return updatedCount
}
